import { type LinearCsticValue, type LinearSubstep, SimpleCsticId } from "../../../Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "../../../Linear/components/SubstepFrame";
import { getProductCode } from "../../../Linear/redux/selectors";
import { LinearSection, LinearSectionTitle } from "../../../Linear/components/Substeps/GuidewaySetStep";
import { type RootState } from "../../../../redux/store";
import { updateCsticValues } from "../../../Linear/redux/actions";
import { SubstepType } from "../../../Linear/dtos";
import { Col, Row } from "../../../../common/components/Layout";
import LanguageAwareExternalContent from "../../../Linear/components/LanguageAwareExternalContent";
import { Card } from "../../../Linear/components/Card";
import {
  CARRIAGE_DETAILS_INFO_MSG,
  FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
} from "../../carriageConfiguratorConstants";

import { Button } from "../../../../common/components/Button/Button";
import { LinearTranslation } from "../../../common/components/LinearTranslation";
import { RESET_STANDARD } from "../../../common/advancedTranslationUtils";
import { KitInfoWrapper } from "../../../Linear/components/Substeps/CarriageDetailsSubstep";
import { KmatType } from "~/Configurators/Linear/getKmatType";
import { Alert, Grid, Stack } from "@mui/material";
import { SimpleCsticSelectField } from "~/Configurators/Shaft/components/SimpleCsticSelectField";
import { CsticNames } from "./constants";
import { Star } from "@mui/icons-material";
import { SelectLubricationConnectorPopup } from "./SelectLubricationConnectorPopup";
import { selectMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { CARRIAGE_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { csticHasValue } from "~/Configurators/common/v2/helper";
import type { CsticUpdate } from "~/Configurators/Linear/api/sendLinearConfiguratorUpdate";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const CarriageDetailsSubstep = ({ def, ...props }: Props) => {
  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      {def.footerInfo.length > 0 && (
        <div className="mb-3">
          {def.footerInfo.map((info) => (
            <Alert key={info} className="mb-2" severity="info">
              <LanguageAwareExternalContent content={info} loadedClassname={"scha__lc__external-label"} />
            </Alert>
          ))}
        </div>
      )}

      <div className={"w-100 mb-30"}>
        <Row className={"mt-30"}>
          <Col size={4}>
            <SingleTile carriageIndex={0} index={0} guidewayIndex={0} guidewayAmount={1} carriageAmount={1} def={def} />
          </Col>
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default CarriageDetailsSubstep;

interface TileProps {
  carriageIndex: number; // Carriage index in redux - 0 based
  guidewayIndex: number; // Guideway index in redux - 0 based
  index: number; // Index of carriage in guideway - 0 based
  guidewayAmount: number;
  carriageAmount: number;
  def: LinearSubstep;
}

const SingleTile = ({ def }: TileProps) => {
  const dispatch = useDispatch();
  const mainKmat = useSelector(selectMainKmat);
  const cstics = mainKmat.cstics;

  const leftCsticHelper = def.inputCstics[CsticNames.KITLeft];
  const leftCstic = cstics[leftCsticHelper.name];

  const middleCsticHelper = def.inputCstics[CsticNames.KITCenter];
  const middleCstic = cstics[middleCsticHelper.name];

  const rightCsticHelper = def.inputCstics[CsticNames.KITRight];
  const rightCstic = cstics[rightCsticHelper.name];

  const lubricationOrientationHelper = def.inputCstics[CsticNames.LubricationConnectorOrientation];

  const lubricationPositionHelper = def.inputCstics[CsticNames.LubricationConnectorPosition];
  const lubricationConnectorPositionCstic = cstics[lubricationPositionHelper.name];

  const lubricationConnectorHelper = def.inputCstics[CsticNames.LubricationConnector];

  const lubricationConnectorAlignmentHelper = def.inputCstics[CsticNames.LubricationConnectorAlignment];
  const lubricationConnectorAlignmentCstic = cstics[lubricationConnectorAlignmentHelper.name];

  const upperDisclaimer = useSelector(selectUpperDisclaimer);

  const standardKitsHelper = def.helperCstics[CsticNames.StandardKits];

  const productCode = useSelector(getProductCode);
  const isClassicCarriage = productCode === CARRIAGE_CONFIGURATOR_PRODUCT_CODE;

  const isEditButtonEnabled = csticHasValue(mainKmat, def.helperCstics[CsticNames.EnableCarriageEditButton].name);

  const editDetails = () =>
    dispatch(
      updateCsticValues(
        [
          isClassicCarriage && {
            type: KmatType.CARRIAGE,
            value: "true",
            key: FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
          },
          {
            type: isClassicCarriage ? KmatType.CARRIAGE : KmatType.DEFAULT,
            value: isClassicCarriage ? null : "N",
            key: standardKitsHelper.name,
          },
        ].filter(Boolean) as CsticUpdate[],
        SubstepType.ConfigureCarriage,
      ),
    );
  const resetDetails = () =>
    dispatch(
      updateCsticValues(
        [
          isClassicCarriage && {
            type: KmatType.CARRIAGE,
            value: "false",
            key: FAKE_CSTIC_ENABLE_CONFIGURE_CARRIAGE_VALUES,
          },
          {
            type: isClassicCarriage ? KmatType.CARRIAGE : KmatType.DEFAULT,
            value: "S",
            key: standardKitsHelper.name,
          },
        ].filter(Boolean) as CsticUpdate[],
        SubstepType.ConfigureCarriage,
      ),
    );

  return (
    <Card disableHover={true} className={"pt-3"}>
      <div className={"px-gutter-half"}>
        {upperDisclaimer && (
          <Alert className="mb-30" severity="info">
            <LanguageAwareExternalContent content={upperDisclaimer} loadedClassname={"scha__lc__external-label"} />
          </Alert>
        )}

        <LinearSectionTitle className={"mt-0 mb-3 "} info={def.extraTexts["sealingAndLubricationSectionInfo"]}>
          <LanguageAwareExternalContent
            content={def.extraTexts["sealingAndLubricationSectionName"]}
            loadedClassname={"scha__lc__external-label"}
          />
        </LinearSectionTitle>

        <LinearSection label={""}>
          <Grid columns={3} gap={"var(--gutter)"} container sx={{ width: "100%" }}>
            <Grid size={1}>
              <KitInfoWrapper cstic={leftCstic} addModalParam={true}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={SimpleCsticId(leftCsticHelper.name)}
                  disabled={leftCsticHelper.disabled}
                  required={leftCsticHelper.required}
                  substepType={SubstepType.ConfigureCarriage}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>

            <Grid size={1}>
              <KitInfoWrapper cstic={middleCstic} addModalParam={true} hidden={!middleCsticHelper.visible}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={SimpleCsticId(middleCsticHelper.name)}
                  disabled={middleCsticHelper.disabled}
                  required={middleCsticHelper.required}
                  substepType={SubstepType.ConfigureCarriage}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>

            <Grid size={1}>
              <KitInfoWrapper cstic={rightCstic} addModalParam={true} hidden={!rightCsticHelper.visible}>
                <SimpleCsticSelectField
                  useCsticLabel
                  csticId={SimpleCsticId(rightCsticHelper.name)}
                  disabled={rightCsticHelper.disabled}
                  required={rightCsticHelper.required}
                  substepType={SubstepType.ConfigureCarriage}
                  renderValue={renderDropdownEntryWithStar}
                  shrink={true}
                  showFilter={true}
                />
              </KitInfoWrapper>
            </Grid>
          </Grid>
        </LinearSection>

        {lubricationOrientationHelper.visible && (
          <LinearSection
            info={def.extraTexts["lubricationConnectorSectionInfo"]}
            label={
              <LanguageAwareExternalContent
                content={def.extraTexts["lubricationConnectorSectionName"]}
                loadedClassname={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={SimpleCsticId(lubricationOrientationHelper.name)}
              disabled={lubricationOrientationHelper.disabled}
              required={lubricationOrientationHelper.required}
              substepType={SubstepType.ConfigureCarriage}
              renderValue={renderDropdownEntryWithStar}
            />
          </LinearSection>
        )}

        {lubricationPositionHelper.visible && (
          <LinearSection
            info={lubricationConnectorPositionCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationConnectorPositionCstic.label}
                loadedClassname={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={SimpleCsticId(lubricationPositionHelper.name)}
              disabled={lubricationPositionHelper.disabled}
              required={lubricationPositionHelper.required}
              substepType={SubstepType.ConfigureCarriage}
              renderValue={renderDropdownEntry}
            />
          </LinearSection>
        )}

        {lubricationConnectorHelper.visible && <SelectLubricationConnectorPopup className={"mb-30"} def={def} />}

        {lubricationConnectorAlignmentHelper.visible && (
          <LinearSection
            info={lubricationConnectorAlignmentCstic.info}
            label={
              <LanguageAwareExternalContent
                content={lubricationConnectorAlignmentCstic.label}
                loadedClassname={"scha__lc__external-label"}
              />
            }
          >
            <SimpleCsticSelectField
              csticId={SimpleCsticId(lubricationConnectorAlignmentHelper.name)}
              disabled={lubricationConnectorAlignmentHelper.disabled}
              required={lubricationConnectorAlignmentHelper.required}
              substepType={SubstepType.ConfigureCarriage}
              renderValue={renderDropdownEntry}
            />
          </LinearSection>
        )}

        <Stack direction={"row"} gap={2} sx={{ pb: 2 }}>
          <Button color={"secondary"} fullWidth={true} onClick={resetDetails} disabled={!isEditButtonEnabled}>
            <LinearTranslation translationKey={RESET_STANDARD} />
          </Button>
          <Button color={"primary"} fullWidth={true} onClick={editDetails} disabled={!isEditButtonEnabled}>
            <LanguageAwareExternalContent
              content={def.extraTexts["edit"]}
              loadedClassname={"scha__lc__external-label"}
            />
          </Button>
        </Stack>
      </div>
    </Card>
  );
};

const renderDropdownEntryWithStar = (value: LinearCsticValue) => (
  <Stack direction="row" gap={0.5} alignItems={"center"}>
    <Star fontSize="small" visibility={value.standard ? undefined : "hidden"} />
    <LanguageAwareExternalContent content={value.label} />
  </Stack>
);
const renderDropdownEntry = (value: LinearCsticValue) => <LanguageAwareExternalContent content={value.label} />;

const selectUpperDisclaimer = (state: RootState) =>
  state.linearConfigurator.specialTranslations[CARRIAGE_DETAILS_INFO_MSG];
